import { useQueryClient } from '@tanstack/react-query';
import {
	getAllChatRooms,
	getOneChatRoom,
	createUpdateChatRoom,
	deleteChatRoom,
	getChatRoomMembers,
	addUserToChatRoom,
	updateChatRoomMember,
	removeUserFromChatRoom,
	muteUser,
	unmuteUser,
	banUser,
	unbanUser,
	promoteUser,
	getAvailableChatRooms,
	getNotDeliveredMessagesCountForUser,
	joinChatRoom,
	leaveChatRoom,
	createDirectMessage,
	getUserDirectMessages,
	getChatUsers,
	getChatRoomUsers,
	getLinkedObjectsWChatRoomsForUser,
} from '../../Services/admin/chat';
import {
	ChatRoom,
	ChatRoomMember,
	CreateChatRoomRequest,
	AddUserToChatRoomRequest,
	MuteUserRequest,
	BanUserRequest,
	PromoteUserRequest,
} from '../../Types/admin/chat';
import { ObjectType } from '../../Types/base';
import { useBizMutation, useBizQuery } from '../useBizQuery';

const QUERYKEY_ADMIN_CHAT_ROOMS = 'ADMIN_CHAT_ROOMS';
const QUERYKEY_ADMIN_CHAT_ROOM = 'ADMIN_CHAT_ROOM';
const QUERYKEY_ADMIN_CHAT_ROOM_MEMBERS = 'ADMIN_CHAT_ROOM_MEMBERS';
const QUERYKEY_USER_AVAILABLE_CHAT_ROOMS = 'USER_CHAT_AVAILABLE_CHAT_ROOMS';
const QUERYKEY_USER_NOT_DELIVERED_MESSAGES_COUNT = 'USER_CHAT_NOT_DELIVERED_MESSAGES_COUNT';
const QUERYKEY_USER_DIRECT_MESSAGES = 'USER_CHAT_DIRECT_MESSAGES';
const QUERYKEY_USER_CHAT_USERS = 'USER_CHAT_USERS';
const QUERYKEY_USER_CHAT_ROOM_USERS = 'USER_CHAT_ROOM_USERS';
const QUERYKEY_USER_LINKED_OBJECTS = 'USER_CHAT_LINKED_OBJECTS';

// Admin hooks
export const useGetAllChatRooms = () => {
	const { data, statusState } = useBizQuery([QUERYKEY_ADMIN_CHAT_ROOMS], () => getAllChatRooms());
	return { data, statusState };
};

export const useGetOneChatRoom = (id: number) => {
	const { data, statusState } = useBizQuery([QUERYKEY_ADMIN_CHAT_ROOM, id], () => getOneChatRoom({ id }));
	return { data, statusState };
};

export const useCreateUpdateChatRoom = () => {
	const wrapperFn = (params: { chatRoom: CreateChatRoomRequest | ChatRoom }) => {
		return createUpdateChatRoom(params.chatRoom);
	};
	const { mutate, statusState, data } = useBizMutation(wrapperFn, {
		getQueryKeyForInvalidation: () => [[QUERYKEY_ADMIN_CHAT_ROOMS], [QUERYKEY_ADMIN_CHAT_ROOM]],
	});

	return {
		createUpdateChatRoom: (chatRoom: CreateChatRoomRequest | ChatRoom) => mutate({ chatRoom }),
		result: data,
		statusState,
	};
};

export const useDeleteChatRoom = () => {
	const wrapperFn = (params: { id: number }) => {
		return deleteChatRoom(params);
	};
	const { mutate, statusState } = useBizMutation(wrapperFn, {
		getQueryKeyForInvalidation: () => [[QUERYKEY_ADMIN_CHAT_ROOMS], [QUERYKEY_ADMIN_CHAT_ROOM]],
	});

	return {
		deleteChatRoom: (id: number) => mutate({ id }),
		statusState,
	};
};

export const useGetChatRoomMembers = (chatRoomId: number) => {
	const { data, statusState } = useBizQuery([QUERYKEY_ADMIN_CHAT_ROOM_MEMBERS, chatRoomId], () =>
		getChatRoomMembers({ chatRoomId })
	);
	return { data, statusState };
};

export const useAddUserToChatRoom = () => {
	const wrapperFn = (params: AddUserToChatRoomRequest) => {
		return addUserToChatRoom(params);
	};
	const { mutate, statusState, data } = useBizMutation(wrapperFn, {
		getQueryKeyForInvalidation: () => [[QUERYKEY_ADMIN_CHAT_ROOM_MEMBERS]],
	});

	return {
		addUserToChatRoom: (request: AddUserToChatRoomRequest) => mutate(request),
		result: data,
		statusState,
	};
};

export const useUpdateChatRoomMember = () => {
	const wrapperFn = (params: { member: ChatRoomMember }) => {
		return updateChatRoomMember(params.member);
	};
	const { mutate, statusState } = useBizMutation(wrapperFn, {
		getQueryKeyForInvalidation: () => [[QUERYKEY_ADMIN_CHAT_ROOM_MEMBERS]],
	});

	return {
		updateChatRoomMember: (member: ChatRoomMember) => mutate({ member }),
		statusState,
	};
};

export const useRemoveUserFromChatRoom = () => {
	const wrapperFn = (params: { chatRoomId: number; userId: number }) => {
		return removeUserFromChatRoom(params);
	};
	const { mutate, statusState } = useBizMutation(wrapperFn, {
		getQueryKeyForInvalidation: () => [[QUERYKEY_ADMIN_CHAT_ROOM_MEMBERS]],
	});

	return {
		removeUserFromChatRoom: (chatRoomId: number, userId: number) => mutate({ chatRoomId, userId }),
		statusState,
	};
};

export const useMuteUser = () => {
	const wrapperFn = (params: MuteUserRequest) => {
		return muteUser(params);
	};
	const { mutate, statusState } = useBizMutation(wrapperFn, {
		getQueryKeyForInvalidation: () => [[QUERYKEY_ADMIN_CHAT_ROOM_MEMBERS]],
	});

	return {
		muteUser: (request: MuteUserRequest) => mutate(request),
		statusState,
	};
};

export const useUnmuteUser = () => {
	const wrapperFn = (params: { chatRoomId: number; userId: number }) => {
		return unmuteUser(params);
	};
	const { mutate, statusState } = useBizMutation(wrapperFn, {
		getQueryKeyForInvalidation: () => [[QUERYKEY_ADMIN_CHAT_ROOM_MEMBERS]],
	});

	return {
		unmuteUser: (chatRoomId: number, userId: number) => mutate({ chatRoomId, userId }),
		statusState,
	};
};

export const useBanUser = () => {
	const wrapperFn = (params: BanUserRequest) => {
		return banUser(params);
	};
	const { mutate, statusState } = useBizMutation(wrapperFn, {
		getQueryKeyForInvalidation: () => [[QUERYKEY_ADMIN_CHAT_ROOM_MEMBERS]],
	});

	return {
		banUser: (request: BanUserRequest) => mutate(request),
		statusState,
	};
};

export const useUnbanUser = () => {
	const wrapperFn = (params: { chatRoomId: number; userId: number }) => {
		return unbanUser(params);
	};
	const { mutate, statusState } = useBizMutation(wrapperFn, {
		getQueryKeyForInvalidation: () => [[QUERYKEY_ADMIN_CHAT_ROOM_MEMBERS]],
	});

	return {
		unbanUser: (chatRoomId: number, userId: number) => mutate({ chatRoomId, userId }),
		statusState,
	};
};

export const usePromoteUser = () => {
	const wrapperFn = (params: PromoteUserRequest) => {
		return promoteUser(params);
	};
	const { mutate, statusState } = useBizMutation(wrapperFn, {
		getQueryKeyForInvalidation: () => [[QUERYKEY_ADMIN_CHAT_ROOM_MEMBERS]],
	});

	return {
		promoteUser: (request: PromoteUserRequest) => mutate(request),
		statusState,
	};
};

// User hooks
export const useGetAvailableChatRooms = (objectType?: ObjectType, objectId?: number) => {
	const { data, statusState } = useBizQuery([QUERYKEY_USER_AVAILABLE_CHAT_ROOMS, objectType, objectId], () =>
		getAvailableChatRooms(objectType, objectId)
	);
	return { data, statusState };
};

export const useGetNotDeliveredMessagesCountForUser = (enabled = true) => {
	const { data, statusState } = useBizQuery(
		[QUERYKEY_USER_NOT_DELIVERED_MESSAGES_COUNT],
		getNotDeliveredMessagesCountForUser,
		enabled
	);
	return { data, statusState };
};

export const useGetLinkedObjects = () => {
	const { data, statusState } = useBizQuery([QUERYKEY_USER_LINKED_OBJECTS], () => getLinkedObjectsWChatRoomsForUser());
	return { data, statusState };
};

export const useGetChatUsers = (objectType?: ObjectType, objectId?: number) => {
	const { data, statusState } = useBizQuery([QUERYKEY_USER_CHAT_USERS, objectType, objectId], () =>
		getChatUsers(objectType, objectId)
	);
	return { data, statusState };
};

export const useGetChatRoomUsers = (chatRoomId: number, enabled: boolean) => {
	const { data, statusState, refetch } = useBizQuery(
		[QUERYKEY_USER_CHAT_ROOM_USERS, chatRoomId],
		() => getChatRoomUsers({ chatRoomId }),
		enabled,
		300
	);
	return { data, statusState, refetch };
};

export const useJoinChatRoom = () => {
	const wrapperFn = (params: { chatRoomId: number }) => {
		return joinChatRoom(params);
	};
	const { mutate, statusState, data } = useBizMutation(wrapperFn, {
		getQueryKeyForInvalidation: () => [[QUERYKEY_USER_AVAILABLE_CHAT_ROOMS]],
	});

	return {
		joinChatRoom: (chatRoomId: number) => mutate({ chatRoomId }),
		result: data,
		statusState,
	};
};

export const useLeaveChatRoom = () => {
	const wrapperFn = (params: { chatRoomId: number }) => {
		return leaveChatRoom(params);
	};
	const { mutate, statusState } = useBizMutation(wrapperFn, {
		getQueryKeyForInvalidation: () => [[QUERYKEY_USER_AVAILABLE_CHAT_ROOMS]],
	});

	return {
		leaveChatRoom: (chatRoomId: number) => mutate({ chatRoomId }),
		statusState,
	};
};

const DO_NOT_LINK_DM_TO_OBJECT = true; //might be something we want in the future

export const useCreateDirectMessage = (objectType?: ObjectType, objectId?: number) => {
	const wrapperFn = (params: { targetUserId: number }) => {
		return createDirectMessage(
			params.targetUserId,
			DO_NOT_LINK_DM_TO_OBJECT ? undefined : objectType,
			DO_NOT_LINK_DM_TO_OBJECT ? undefined : objectId
		);
	};
	const { mutate, statusState, data, variables } = useBizMutation(wrapperFn, {
		getQueryKeyForInvalidation: () => {
			return [[QUERYKEY_USER_DIRECT_MESSAGES], [QUERYKEY_USER_AVAILABLE_CHAT_ROOMS]];
		},
	});

	return {
		createDirectMessage: (targetUserId: number) => mutate({ targetUserId }),
		result: data,
		statusState,
		directMessageToUserId: variables?.targetUserId,
	};
};

export const useGetUserDirectMessages = () => {
	const { data, statusState } = useBizQuery([QUERYKEY_USER_DIRECT_MESSAGES], getUserDirectMessages);
	return { data, statusState };
};

export const useClearAvailableChatRoomsCache = () => {
	const queryClient = useQueryClient();
	const clearAvailableChatRoomsCache = () => {
		queryClient.invalidateQueries({ queryKey: [QUERYKEY_USER_AVAILABLE_CHAT_ROOMS] });
	};
	return clearAvailableChatRoomsCache;
};
